import { DetailsList, Panel, PanelType, Persona, TextField, Selection, CommandBar, SelectionMode, Icon, TooltipHost, Dialog, Dropdown, IDropdownOption } from "office-ui-fabric-react";
import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import { getRoles, searchUsersByRole } from "../../../api/userInfo";
import { IUserWithPhoto } from "../../../types/User.types";

import styles from "./AdministratorsPanel.module.scss";
import { Spinner } from "@fluentui/react";
import { RoleAssignmentDialog } from "./RoleAssignment";
import { TagPicker } from "../../TagPicker";
import { TagType } from "../../../types/Tagging.types";
import { assignTags } from "../../../api/tagging";

export interface IUserManagementPanelProps {
    onDismiss: () => void;
}

export const UserManagementPanel = (props: IUserManagementPanelProps) => {

    const [ waiting, setWaiting ] = useState(false);
    const [ userSearch, setUserSearch ] = useState<string|undefined>();
    const [ searchResults, setSearchResults ] = useState<IUserWithPhoto[]>([]);
    const [ itemsSelected, setItemsSelected ] = useState<boolean>(false);
    const [ assignRolesToUserIdx, setAssignRolesToUserIdx ] = useState<number|undefined>();
    const [ assignTagsToUserIdx, setAssignTagsToUserIdx ] = useState<number|undefined>();
    const [ tagReady, setTagReady ] = useState(false);
    const [ roleOptions, setRoleOptions ] = useState<IDropdownOption[]>([]);
    const [ selectedRoleId, setSelectedRoleId ] = useState<number|undefined>();

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as IUserWithPhoto[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setItemsSelected(true);
        } else {
            setItemsSelected(false);
        }
    };
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const updateSearch = useCallback(debounce(async (searchStr: string | undefined, roleId: number | undefined) => {// eslint-disable-line react-hooks/exhaustive-deps
        setWaiting(true);
        const results = await searchUsersByRole(roleId, searchStr);
        setSearchResults(results);
        setWaiting(false);
    }, 500), []);

    useEffect(() => {
        updateSearch(userSearch, selectedRoleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearch, selectedRoleId]);

    useEffect(() => {
        const loadRoleOptions = async () => {
            const roles = await getRoles();
            if (roles instanceof Error) {
                return;
            }
            const options: IDropdownOption[] = [{ key: "all", text: "All" }];
            roles.forEach((role) => {
                options.push({ key: role.Id, text: role.Name });
            });
            setRoleOptions(options);
        };
        loadRoleOptions();
    }, []);

    const getIconOrRoleName = (roleName: string) => {
        const icons: Record<string,string> = {
            "Admin": "PlayerSettings",
            "Reporting": "PowerBILogo16",
            "Booking Manager": "Calendar",
            "Not Restricted": "Unlock"
        };
        if (icons[roleName]) {
            return <TooltipHost content={roleName}>
                <Icon iconName={icons[roleName]} />
            </TooltipHost>;
        } else {
            return <div>{roleName}</div>
        }
    };

    let assignTagsToUser: IUserWithPhoto | undefined = undefined;
    if (assignTagsToUserIdx !== undefined) {
        assignTagsToUser = searchResults[assignTagsToUserIdx];
    }

    let assignRolesToUser: IUserWithPhoto | undefined = undefined;
    if (assignRolesToUserIdx !== undefined) {
        assignRolesToUser = searchResults[assignRolesToUserIdx];
    }    

    return (
        <Panel isOpen={true} type={PanelType.medium} headerText="Manage Users" onDismiss={props.onDismiss}>
            <Dropdown
                label="Filter By Role"
                placeholder="Select a role"
                options={roleOptions}
                selectedKey={selectedRoleId !== undefined ? selectedRoleId : "all"}
                onChange={(event, option) => {
                    if (!option || option.key === "all") {
                        setSelectedRoleId(undefined);
                        return;
                    }
                    setSelectedRoleId(option.key as number);
                }}
            />
            <TextField label="Search Users" iconProps={{iconName: "Search"}} value={userSearch} onChange={(e, newValue) => setUserSearch(newValue)} />
            <CommandBar
                styles={{
                    root: {
                        marginTop: 20,
                        backgroundColor: "#e3e3e3",
                        " .ms-Button": {
                            backgroundColor: "#e3e3e3",
                        },
                        " .ms-Button:hover": {
                            backgroundColor: "#f4f4f4",
                        },
                    },
                }}
                items={[
                    {
                        key: "assignRoles",
                        disabled: waiting === true || !itemsSelected,
                        text: "Assign Roles",
                        onClick: () => { setAssignRolesToUserIdx(selection.getSelectedIndices()[0]) }
                    },
                    {
                        key: "assignTags",
                        disabled: waiting === true || !itemsSelected,
                        text: "Tag User",
                        onClick: () => { setAssignTagsToUserIdx(selection.getSelectedIndices()[0])}
                    }
                ]}
                farItems={[
                    // {
                    //     disabled: waiting === true,
                    //     key: "export",
                    //     iconOnly: true,
                    //     iconProps: {
                    //         iconName: "Export"
                    //     },
                    //     title: "Export list of administrators",
                    //     href: "/api/users/admins/export",
                    //     target: "_blank"
                    // },
                    {
                        disabled: waiting === true,
                        key: "exportRoleUsers",
                        iconOnly: true,
                        iconProps: {
                            iconName: "Download"
                        },
                        title: "Export all users with roles",
                        href: "/api/users/roles/export",
                        target: "_blank"
                    }
                ]}
            />
            { waiting === true && (
                <Spinner />
            )}
            { waiting === false && searchResults.length > 0 && (
                <DetailsList
                    items={searchResults}
                    columns={[
                        {
                            key: "name",
                            name: "Name",
                            fieldName: "FullName",
                            minWidth: 30,
                            onRender: (item, index, column) => {
                                let photo: string | undefined = undefined;
                                if (item.Photo) {
                                    photo = `data:${item.Photo.Type};base64,${item.Photo.Data}`;
                                }
                                return (
                                    <Persona text={item.FullName} secondaryText={item.Email} imageUrl={photo} />
                                )
                            }
                        },
                        {
                            key: "roles",
                            isIconOnly: true,
                            name: "Roles",
                            minWidth: 80,
                            className: styles.isAdminColumn,
                            onRender: (item: IUserWithPhoto, index, column) => {
                                if (item.Roles && item.Roles.length > 0) {
                                    return item.Roles.map((role) => getIconOrRoleName(role.Name));
                                }
                            }
                        },
                        {
                            key: "tags",
                            isIconOnly: true,
                            name: "Tags",
                            minWidth: 80,
                            className: styles.isAdminColumn,
                            onRender: (item: IUserWithPhoto, index, column) => {
                                if (item.Tags && item.Tags.length > 0) {
                                    return (
                                        <TooltipHost tooltipProps={{ onRenderContent: (tooltipProps) => <>
                                            { item.Tags?.map(t => <div>{t.Label}</div>) }
                                        </>}}>
                                            <Icon iconName="Tag" />
                                        </TooltipHost>
                                    )
                                }
                            }
                        }
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.single}
                />
            )}
            { assignRolesToUser !== undefined && (
                <RoleAssignmentDialog 
                    user={assignRolesToUser}
                    onDismiss={(saved) => {
                        setAssignRolesToUserIdx(undefined);
                        if (saved) updateSearch(userSearch, selectedRoleId);
                    }}
                />
            )}
            { assignTagsToUser !== undefined && (
                <Dialog hidden={false} dialogContentProps={{ title: "Assign Tags", subText: "Create and apply tags to users."}} onDismiss={() => {
                    setAssignTagsToUserIdx(undefined);
                    updateSearch(userSearch, selectedRoleId);
                }}
                >
                    <TagPicker
                        label="User Tags"
                        type={TagType.User}
                        onChange={async (tags) => {
                            if (assignTagsToUser) {
                                await assignTags(assignTagsToUser.Id, "user", tags);
                            }
                        }}
                        selectedTags={assignTagsToUser.Tags}
                    />
                </Dialog>
            )}
        </Panel>
    );
}